import { ApplicationEntity } from "@/model/entity/application-entity"
import { Shipment } from "@/model/entity/shipment/shipment"
import { FieldDescriptor, FieldTypeEnum } from "../field-descriptor"
import { ParcelCOD } from "./parcel-cod"
import { parcelStatusEnumList } from "@/model/enum/parcel-status-enum"
import { Employee } from "@/model/entity/employee/employee"
import { Expose, Type } from "class-transformer"
import { ParcelGoods } from "../shipment/parcel-goods"
import { ParcelLimitedQuantity } from "./parcel-limited-quantity"

export class Parcel extends ApplicationEntity {
  @FieldDescriptor({
    fieldType: FieldTypeEnum.DATE,
    fieldPrettyName: "entity.parcel.parcel_date",
    columnWidth: "80",
    hasTime: true,
    fixed: true,
    sortable: true,
    defaultSort: "descending"
  })
  parcelDate?: number

  @FieldDescriptor({
    fieldType: FieldTypeEnum.DATE,
    fieldPrettyName: "entity.parcel.updated_date",
    hasTime: true,
    displayed: false
  })
  updateDate?: number

  @Expose({ name: "number" })
  _number: any

  @FieldDescriptor({
    fieldType: FieldTypeEnum.TEXT,
    fieldPrettyName: "entity.parcel.parcel_number",
    columnWidth: "120",
    isFilterable: true
  })
  get number() {
    return exists(this._number) ? this._number!.toString().padStart(14, "0") : ""
  }

  @FieldDescriptor({
    fieldType: FieldTypeEnum.TEXT,
    fieldPrettyName: "entity.parcel.reference",
    columnWidth: "85",
    isFilterable: true
  })
  parcelReference?: string

  @FieldDescriptor({
    fieldType: FieldTypeEnum.TEXT,
    fieldPrettyName: "entity.parcel.reference2",
    columnWidth: "85",
    isFilterable: true
  })
  parcelReference2?: string

  @FieldDescriptor({ fieldType: FieldTypeEnum.TEXT, fieldPrettyName: "entity.parcel.recipient", isFilterable: true })
  get recipient(): string | undefined {
    if (!exists(this.shipment!.shipmentReceiver)) {
      return undefined
    }
    return this.shipment!.shipmentReceiver!.name
  }

  @FieldDescriptor({
    fieldType: FieldTypeEnum.TEXT,
    fieldPrettyName: "entity.parcel.address",
    columnWidth: "90",
    isFilterable: true
  })
  get address(): string | undefined {
    if (!exists(this.shipment!.shipmentReceiver)) {
      return undefined
    }
    return this.shipment!.shipmentReceiver!.address
  }

  @FieldDescriptor({ fieldType: FieldTypeEnum.TEXT, fieldPrettyName: "entity.parcel.weight", columnWidth: "85" })
  get weightKg(): number {
    return this.weight / 1000
  }

  @FieldDescriptor({ fieldType: FieldTypeEnum.TEXT, fieldPrettyName: "entity.parcel.pcs", columnWidth: "45" })
  listRankOf?: string

  @FieldDescriptor({
    fieldType: FieldTypeEnum.TEXT,
    fieldPrettyName: "entity.parcel.type",
    columnWidth: "55",
    isFilterable: true
  })
  get type(): string {
    return this.routingResult.serviceText
  }

  @FieldDescriptor({
    fieldType: FieldTypeEnum.TEXT,
    fieldPrettyName: "entity.parcel.postal_code",
    columnWidth: "100",
    isFilterable: true
  })
  get postalCode(): string | undefined {
    if (!exists(this.shipment!.shipmentReceiver)) {
      return undefined
    }
    return this.shipment!.shipmentReceiver!.country.isoCodeAlpha2 + " " + this.shipment!.shipmentReceiver!.postalCode
  }

  @FieldDescriptor({ fieldType: FieldTypeEnum.TEXT, fieldPrettyName: "entity.parcel.other", columnWidth: "80" })
  listOther?: string

  parcelStatus?: string

  @FieldDescriptor({
    fieldType: FieldTypeEnum.SELECT,
    fieldPrettyName: "entity.parcel.parcel_status",
    columnWidth: "160",
    selectionFieldDescription: {
      displayKey: "i18nKey",
      valueKey: "value",
      values: parcelStatusEnumList,
      i18n: true
    },
    isFilterable: true,
    fixed: true
  })
  statusId?: number

  @Type(() => Shipment)
  shipment?: Shipment

  weight: number

  serviceCode: string
  serviceBackCode: string | null
  rankOf: number
  parcelCOD?: ParcelCOD

  routingResult: any

  rowColor?: string
  statusColumnColor?: string

  get shipmentSource(): string {
    return this.shipment!.shipmentSource
  }

  deletable?: boolean

  printable?: boolean

  selfBookable?: boolean

  sendable?: boolean

  cancellable?: boolean

  additionalCode?: string

  asCode?: string

  parcelLimitedQuantity?: ParcelLimitedQuantity

  // lqUnno?: string
  // lqPgGroup?: string
  // lqCCode?: string
  // lqClass?: string
  // lqSubweight?: string

  @FieldDescriptor({
    fieldType: FieldTypeEnum.TEXT,
    fieldPrettyName: "entity.collection_request.employee",
    displayed: false
  })
  employee?: Employee

  @FieldDescriptor({
    fieldType: FieldTypeEnum.TEXT,
    fieldPrettyName: "entity.parcel.cod_reference",
    columnWidth: "85",
    isFilterable: false
  })
  get codReference(): string | undefined {
    if (exists(this.shipment!.shipmentCOD)) {
      return this.shipment!.shipmentCOD.reference
    }
    return undefined
  }

  @FieldDescriptor({
    fieldType: FieldTypeEnum.TEXT,
    fieldPrettyName: "entity.parcel.cod_amount",
    columnWidth: "85",
    isFilterable: false
  })
  get codAmount(): string | undefined {
    if (exists(this.parcelCOD)) {
      return `${this.parcelCOD!.amount} ${this.shipment!.shipmentCOD.currency || ""}`
    }
    return undefined
  }

  @FieldDescriptor({
    fieldType: FieldTypeEnum.TEXT,
    fieldPrettyName: "entity.parcel.dimension",
    columnWidth: "85",
    isFilterable: true
  })
  dimension?: string

  parcelGoods?: ParcelGoods

  constructor(
    weight: number,
    serviceCode: string,
    serviceBackCode: string | null,
    rankOf: number,
    numberValue?: string,
    parcelCOD?: ParcelCOD,
    parcelDate?: number,
    parcelStatus?: string,
    shipment?: Shipment,
    routingResult?: any,
    listRankOf?: string,
    listOther?: string,
    statusId?: number,
    parcelReference?: string,
    parcelReference2?: string,
    rowColor?: string,
    statusColumnColor?: string,
    deletable?: boolean,
    printable?: boolean,
    selfBookable?: boolean,
    sendable?: boolean,
    cancellable?: boolean,
    updateDate?: number,
    id?: number,
    dimension?: string,
    parcelGoods?: ParcelGoods,
    asCode?: string,
    parcelLimitedQuantity?: ParcelLimitedQuantity
    // lqUnno?: string,
    // lqPgGroup?: string,
    // lqCCode?: string,
    // lqClass?: string,
    // lqSubweight?: string
  ) {
    super(id)
    this.weight = weight
    this._number = numberValue
    this.rankOf = rankOf
    this.serviceCode = serviceCode
    this.serviceBackCode = serviceBackCode
    this.parcelDate = parcelDate
    this.shipment = shipment
    this.parcelStatus = parcelStatus
    this.parcelCOD = parcelCOD
    this.routingResult = routingResult
    this.listRankOf = listRankOf
    this.listOther = listOther
    this.statusId = statusId
    this.parcelReference = parcelReference
    this.parcelReference2 = parcelReference2
    this.rowColor = rowColor
    this.statusColumnColor = statusColumnColor
    this.deletable = deletable
    this.printable = printable
    this.selfBookable = selfBookable
    this.sendable = sendable
    this.cancellable = cancellable
    this.updateDate = updateDate
    this.dimension = dimension
    this.parcelGoods = parcelGoods
    this.asCode = asCode
    this.parcelLimitedQuantity = parcelLimitedQuantity
    // this.lqUnno = lqUnno,
    // this.lqPgGroup = lqPgGroup,
    // this.lqCCode = lqCCode,
    // this.lqClass = lqClass,
    // this.lqSubweight = lqSubweight
  }
}
